import { useEffect, useState } from "react";
import { venueName } from "../venues";
import { balanceDp, Banner, Card, CardHead, Empty, Freshness, Num, Pill, Skeleton } from "../components/ui";
import { IconBalances } from "../components/icons";

interface VenueBalances {
  venue: string;
  environment: string;
  captured_at: string;
  balances: { asset: string; raw_asset: string; amount: string }[];
}

const POLL_MS = 60_000;

async function fetchBalances(): Promise<VenueBalances[]> {
  const res = await fetch("/api/venues/balances", {
    headers: { authorization: `Bearer ${sessionStorage.getItem("otc.session") ?? ""}` },
  });
  if (!res.ok) throw new Error(String(res.status));
  return (await res.json()).venues;
}

export function Balances() {
  const [venues, setVenues] = useState<VenueBalances[] | null>(null);
  const [error, setError] = useState("");
  const [at, setAt] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    const load = () =>
      fetchBalances()
        .then((v) => {
          if (!alive) return;
          setVenues(v);
          setAt(Date.now());
          setError("");
        })
        .catch(() => alive && setError("Could not load balances."));
    void load();
    const t = setInterval(load, POLL_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  if (!venues && !error) return <Skeleton rows={6} />;

  return (
    <>
      <div className="section-head">
        <p className="page-intro" style={{ marginBottom: 0 }}>
          Live positions held at each liquidity venue, polled every five minutes.
        </p>
        <Freshness at={at} intervalMs={POLL_MS} />
      </div>

      {error && <Banner tone="error">{error}</Banner>}

      {venues?.length === 0 && (
        <Card>
          <Empty
            icon={<IconBalances size={20} />}
            title="No snapshots yet"
            body="The first balance poll lands within five minutes of deploy."
          />
        </Card>
      )}

      <div className="grid grid-2">
        {venues?.map((v) => (
          <Card key={v.venue} pad={false} className="enter">
            <CardHead
              plain
              title={venueName(v.venue)}
              right={v.environment === "sandbox" ? <Pill tone="warn">sandbox</Pill> : <Pill tone="good" dot>live</Pill>}
            />
            {v.balances.length === 0 ? (
              <div style={{ padding: "20px", textAlign: "center" }} className="dim t-sm">
                All balances zero.
              </div>
            ) : (
              <div className="table-wrap flush">
                <table>
                  <thead>
                    <tr>
                      <th>Asset</th>
                      <th className="right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {v.balances.map((b) => (
                      <tr key={b.raw_asset}>
                        <td style={{ fontWeight: 500 }}>
                          {b.asset}
                          {b.raw_asset !== b.asset && <span className="dimmer t-sm"> {b.raw_asset}</span>}
                        </td>
                        <td className="right num"><Num value={b.amount} dp={balanceDp(b.asset, b.amount)} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="t-label" style={{ padding: "10px 20px 14px", borderTop: "1px solid var(--hairline)" }}>
              as of {new Date(v.captured_at).toLocaleString()}
            </div>
          </Card>
        ))}
      </div>
    </>
  );
}
